import React, { useState, useEffect } from 'react'
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  useTheme
} from '@mui/material'

import {
  AddressState,
  AddressFormProps,
  AddressOption,
  Option,
  stateOptions,
  NameFields,
  AddressSuggestion
} from './types'
import { Address } from '../../../domain/address'
import {
  cityValidation,
  numericMatcher
} from '../../../services/formhelper.service'

const AddressForm: React.FC<AddressFormProps> = ({
  loading,
  suggestions,
  formData,
  setFormData,
  setFormError,
  getSuggestions
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [values, setValues] = useState<AddressState>({
    name: { ...formData.name },
    address: { ...formData.address, suggestion: null }
  })

  const addressOptions = suggestions.map((option, index) => ({
    label: option.street,
    value: option,
    id: `address-option-${index}`
  }))

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { suggestion, ...data } = values.address

    if (
      Object.keys(values.address).some(
        (k: keyof Address) => !!values.address[k]
      )
    ) {
      setFormData({ name: { ...values.name }, address: { ...data } })
    }
  }, [values])

  useEffect(() => {
    if (values.address.street) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { suggestion, ...data } = values.address

      getSuggestions(data)
    }
  }, [values.address.street])

  const isValidInput =
    (field: keyof AddressSuggestion) =>
    (value: string): boolean => {
      if (field === 'postal') {
        return numericMatcher(value) || value === ''
      }

      if (field === 'city') {
        return cityValidation(value) || value === ''
      }

      return true
    }

  const handleChange =
    (prop: keyof AddressSuggestion) =>
    (
      event: React.ChangeEvent<HTMLInputElement>,
      option: Option | string | void
    ): void => {
      let value
      if (option) {
        value = typeof option === 'string' ? option : option.value
      } else {
        value = event.target.value
      }

      if (!isValidInput(prop)(value)) {
        return
      }

      setFormError(null)

      setValues({
        ...values,
        address: { ...values.address, [prop]: value || '' }
      })
    }

  const handleNameChange =
    (prop: keyof NameFields) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues({
        ...values,
        name: { ...values.name, [prop]: event.target.value }
      })
    }

  const onAutoSuggestChange = (option: AddressOption): void => {
    setValues({
      ...values,
      address: {
        street: option.value.street,
        refinement: option.value.refinement,
        city: option.value.city,
        state: option.value.state,
        postal: option.value.postal,
        country: option.value.country,
        suggestion: option
      }
    })
  }

  const renderOption = (
    props: Record<string, unknown>,
    option: AddressOption
  ) => {
    const { refinement, city, state, postal } = option.value
    const additionalOptionData = [
      ...(refinement ? [refinement] : []),
      city,
      state,
      postal
    ].join(', ')

    return (
      <Box
        component="li"
        {...props}
        id={option.id}
        key={option.id}
        sx={{
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignItems: 'center'
          }}
        >
          <Typography>{option.label}</Typography>
          <Typography component="p" variant="caption">
            {additionalOptionData}
          </Typography>
        </div>
      </Box>
    )
  }

  return (
    <>
      <div style={{ marginBottom: 30 }}>
        <Typography component="div" variant="body1" align="left" gutterBottom>
          What’s your full legal name?
        </Typography>

        <div
          style={{
            display: 'flex',
            flexFlow: 'row wrap',
            justifyContent: 'flex-start',
            alignContent: 'space-around',
            rowGap: 10,
            gap: 20
          }}
        >
          <TextField
            required
            id="givenName"
            label="First name"
            value={values.name.givenName || ''}
            onChange={handleNameChange('givenName')}
            sx={{ flex: 1, minWidth: 100 }}
          />

          <TextField
            id="middleName"
            label="Middle name"
            value={values.name.middleName || ''}
            onChange={handleNameChange('middleName')}
            sx={{ flex: 1, minWidth: 100 }}
          />

          <TextField
            required
            id="familyName"
            label="Last name"
            value={values.name.familyName || ''}
            onChange={handleNameChange('familyName')}
            sx={{ flex: 1, minWidth: 100 }}
          />
        </div>
      </div>

      <Typography component="div" variant="body1" align="left" gutterBottom>
        What's your home address? This is where we'll ship your card.
      </Typography>

      <div
        style={{
          display: 'flex',
          flexFlow: 'row wrap',
          justifyContent: 'flex-start',
          alignContent: 'space-around',
          rowGap: 10,
          gap: 20
        }}
      >
        <Autocomplete
          id="address-select"
          freeSolo
          filterOptions={(x: any) => x}
          options={addressOptions}
          loading={loading}
          value={
            values.address.suggestion
              ? values.address.suggestion
              : values.address.street
          }
          inputValue={values.address.street}
          onChange={(e, option: AddressOption) =>
            option && onAutoSuggestChange(option)
          }
          onInputChange={handleChange('street')}
          renderOption={renderOption}
          sx={{ flex: 1, minWidth: 100 }}
          open={open}
          onOpen={() => {
            setOpen(true)
          }}
          onClose={() => {
            setOpen(false)
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Address"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {open && loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                )
              }}
            />
          )}
        />
        <TextField
          id="secondary"
          label="Apt, building, unit (optional)"
          value={values.address.refinement}
          onChange={handleChange('refinement')}
          sx={{ flex: 1, minWidth: 100 }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexFlow: 'row wrap',
          justifyContent: 'flex-start',
          alignContent: 'space-around',
          rowGap: 10,
          gap: 20
        }}
      >
        <TextField
          required
          id="city"
          label="City"
          value={values.address.city}
          onChange={handleChange('city')}
          sx={{ flex: 1, minWidth: 100 }}
        />
        <Autocomplete
          id="state-select"
          options={stateOptions}
          onChange={handleChange('state')}
          value={
            stateOptions.filter(
              (option) => option.label === values.address.state
            )[0]
          }
          sx={{ flex: 1, minWidth: 100 }}
          renderInput={(params) => (
            <TextField required label="State/Territory" {...params} />
          )}
        />
        <TextField
          required
          id="zipcode"
          label="ZIP code"
          value={values.address.postal}
          onChange={handleChange('postal')}
          sx={{ flex: 1, minWidth: 100 }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexFlow: 'row wrap',
          justifyContent: 'flex-start',
          alignContent: 'space-around',
          rowGap: 10,
          gap: 20
        }}
      >
        <TextField
          id="country"
          label="Country"
          disabled
          required
          value={values.address.country}
          sx={{ flex: 1, minWidth: 100 }}
        />
        <Typography
          component="p"
          variant="caption"
          sx={{ mb: theme.spacing(1), flex: 2 }}
        >
          Currently, we are only available in the United States. PO Box, PMB,
          business, temporary, and non-US based addresses are not accepted.
        </Typography>
      </div>
    </>
  )
}

export default AddressForm
